import { useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { CommentItem, CommentItemSkeleton } from '@root/entities/comment/ui'
import { selectedNetworkAtom } from '@root/entities/network'

import { reatomComments } from '../../atoms'

import { CommentsHistoryError } from './comments-history-error'
import { CommentsHistoryThreadHeader } from './comments-history-thread-header'

import type { Comment } from '@root/entities/comment/model'

export type CommentsHistoryThreadProps = {
  history: Comment.CommentsHistory
}

export const CommentsHistoryThread = ({
  history,
}: CommentsHistoryThreadProps) => {
  const [network] = useAtom(selectedNetworkAtom)

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomComments({
        baseTokenAddress: history.pair.base.address,
        quoteTokenAddress: history.pair.quote.address,
        pairAddress: history.pair.address,
        network,
        threadInitial: history.comments,
        threadMode: 'dynamic',
      }),
    [history, network],
  )

  const [commentsThread] = useAtom(model.commentsThreadAtom)
  const [loadCommentsRequestState] = useAtom(model.loadCommentsRequestState)

  const renderComments = () => {
    if (AsyncStateN.isLoading(loadCommentsRequestState)) {
      return (
        <Thread>
          <CommentItemSkeleton />
          <CommentItemSkeleton />
        </Thread>
      )
    }
    if (AsyncStateN.isError(loadCommentsRequestState)) {
      return (
        <Thread>
          <CommentsHistoryError />
        </Thread>
      )
    }

    if (commentsThread.comments.length === 0) {
      return
    }

    return (
      <Thread>
        {commentsThread.comments.map((comment) => (
          <CommentItem key={comment.id} comment={comment} model={model} />
        ))}
      </Thread>
    )
  }

  return (
    <Root>
      <CommentsHistoryThreadHeader history={history} model={model} />
      {renderComments()}
    </Root>
  )
}

const Root = styled.div`
  background: var(--background-2);
  z-index: 0;

  border-radius: 10px;
  padding: 2px;
  /* min-height: 200px; */

  display: grid;
  grid-template-rows: auto 1fr;

  --comment-message-background: var(--background-1);
`

const Thread = styled.div`
  display: grid;
  padding: 12px;
  z-index: 1;
`
